import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Modal } from '@/shared/ui/Modal/Modal'
import { fecha, pesos } from '@/shared/utilidades/formato'

import { useSesion } from '@/modules/auth/useSesion'

import { ConceptosCotizacion } from './ConceptosCotizacion'
import { OfrecerEnvioAlCliente } from './OfrecerEnvioAlCliente'
import {
  useCandidatasParaEnlazar,
  useCotizacionWorldOffice,
  useEnlazarCotizacion,
  type CandidataWorldOffice,
} from './useEnlazarCotizacion'
import estilos from './EnlazarCotizacion.module.css'

/**
 * ESCOGER LA COTIZACIÓN DE WORLD OFFICE QUE CORRESPONDE A ESTA ORDEN.
 *
 * Es una pantalla que mueve plata: lo que se escoja aquí es lo que va a decir
 * que vale el trabajo, lo que el cliente va a ver en su enlace y lo que el
 * tablero va a contar como comprometido. Por eso no es una lista de números.
 *
 * Cada fila tiene que dejar comprobar, sin salir de aquí, que es LA correcta:
 *
 *   · EL VALOR, grande. Es el dato con el que se reconoce una cotización —uno
 *     se acuerda de «la de ochocientos noventa mil», no del asiento 2748—.
 *   · A NOMBRE DE QUIÉN. Cuando se busca fuera del cliente de la orden, el
 *     nombre es lo único que separa enlazar bien de enlazarle el trabajo de
 *     otro.
 *   · POR QUÉ SE SUGIERE, escrito. Una recomendación sin razón visible es una
 *     corazonada del sistema, y nadie debería enlazar plata por corazonada
 *     ajena. Cuando la base sugiere, dice qué la hizo sugerir.
 *   · LA DISTANCIA A LA VISITA, y el aviso cuando la cotización es ANTERIOR.
 *     Eso normalmente es señal de que no corresponde... salvo en las órdenes
 *     que entraron por el Excel, donde la fecha de solicitud es la del día del
 *     import y TODAS salen anteriores. Por eso avisa en vez de esconder: la
 *     regla sigue dicha, pero decide la persona, que es la que sabe cuál de
 *     las dos fechas es la de verdad.
 */

type Props = {
  ordenServicioId: string
  /** Para la cabecera: de quién es el trabajo que se está enlazando. */
  cliente?: string | null
  /** Número de la orden, para que se vea qué se está tocando. */
  orden?: string | null
  /**
   * ADICIONAL: lo que apareció a media obra. No reemplaza a la cotización que
   * ya está —esa es el trabajo que la cuadrilla está haciendo— sino que se
   * suma, y no abre una segunda orden de trabajo porque es la misma obra.
   */
  adicional?: boolean
  abierto: boolean
  onCerrar: () => void
}

export function EnlazarCotizacion({
  ordenServicioId,
  cliente,
  orden,
  adicional = false,
  abierto,
  onCerrar,
}: Props) {
  const [busqueda, setBusqueda] = useState('')
  const [soloDelCliente, setSoloDelCliente] = useState(true)
  const [escogida, setEscogida] = useState<CandidataWorldOffice | null>(null)
  // Cuál está desplegada. UNA sola: dos documentos abiertos a la vez obligan a
  // desplazarse para comparar, que es lo contrario de lo que se viene a hacer.
  const [abierta, setAbierta] = useState<number | null>(null)
  // Ya enlazada: el paso opcional de mandársela al cliente.
  const [enlazada, setEnlazada] = useState<{ id: string; numero: string } | null>(null)
  const { puede } = useSesion()

  const {
    data: candidatas = [],
    isLoading,
    error,
  } = useCandidatasParaEnlazar(abierto ? ordenServicioId : null, { soloDelCliente, busqueda })

  const enlazar = useEnlazarCotizacion()

  function cerrar() {
    setEscogida(null)
    setAbierta(null)
    setBusqueda('')
    setSoloDelCliente(true)
    setEnlazada(null)
    enlazar.reset()
    onCerrar()
  }

  async function confirmar() {
    if (!escogida) return
    const id = await enlazar.mutateAsync({
      ordenServicioId,
      idAsiento: escogida.id_asiento,
      adicional,
    })
    // Quien puede aprobar ve el ofrecimiento de enviarla; los demás terminan aquí.
    if (puede('cotizaciones', 'aprobar')) setEnlazada({ id, numero: escogida.numero_visible })
    else cerrar()
  }

  if (enlazada) {
    return (
      <Modal
        abierto={abierto}
        titulo={`${enlazada.numero} quedó enlazada`}
        subtitulo={[orden, cliente].filter(Boolean).join(' · ')}
        onCerrar={cerrar}
        pie={
          <Boton variante="secundario" onClick={cerrar}>
            Listo
          </Boton>
        }
      >
        <p className={estilos.hecho}>Ya cuelga de la orden y pasó a alistamiento en bodega.</p>
        <OfrecerEnvioAlCliente cotizacionId={enlazada.id} numero={enlazada.numero} />
      </Modal>
    )
  }

  return (
    <Modal
      abierto={abierto}
      ancho
      titulo={adicional ? 'Añadir un adicional' : 'Enlazar una cotización de World Office'}
      subtitulo={
        adicional
          ? `${[orden, cliente].filter(Boolean).join(' · ')} · lo que apareció a media obra`
          : [orden, cliente].filter(Boolean).join(' · ') ||
            'Escoja el documento que le pone precio a esta orden'
      }
      onCerrar={cerrar}
      pie={
        <>
          {/* El resumen de lo escogido va PEGADO al botón que lo confirma, no
              arriba en la lista: es lo último que se mira antes de aceptar. */}
          {escogida && (
            <p className={estilos.resumen}>
              Se enlaza <b>{escogida.numero_visible}</b> por{' '}
              <b className={estilos.resumenPlata}>{pesos(escogida.total)}</b>
            </p>
          )}

          <Boton variante="plano" onClick={cerrar} disabled={enlazar.isPending}>
            Cancelar
          </Boton>

          <Boton
            variante="principal"
            onClick={() => void confirmar()}
            disabled={!escogida || enlazar.isPending}
          >
            {enlazar.isPending
              ? 'Enlazando…'
              : adicional
              ? 'Añadir como adicional'
              : 'Enlazar esta cotización'}
          </Boton>
        </>
      }
    >
      <div className={estilos.filtros}>
        <input
          className={estilos.buscador}
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por número o por nombre del cliente"
          aria-label="Buscar cotizaciones de World Office"
        />

        {/* Diez de los veintitrés clientes importados tienen documento
            inventado por el importador, y por documento no cruzan nunca. Sin
            esta casilla esas órdenes no se pueden enlazar de ninguna forma. */}
        <label className={estilos.casilla}>
          <input
            type="checkbox"
            checked={!soloDelCliente}
            onChange={(e) => setSoloDelCliente(!e.target.checked)}
          />
          Buscar en todos los clientes
        </label>
      </div>

      {enlazar.error && (
        <Aviso tipo="error" titulo="No se pudo enlazar">
          {enlazar.error.message}
        </Aviso>
      )}

      {error && (
        <Aviso tipo="error" titulo="No se pudo consultar World Office">
          {error.message}
        </Aviso>
      )}

      {isLoading ? (
        <p className={estilos.vacio}>Buscando en World Office…</p>
      ) : candidatas.length === 0 ? (
        <div className={estilos.vacio}>
          <p className={estilos.vacioTitulo}>
            {soloDelCliente
              ? 'Este cliente no tiene cotizaciones libres en World Office'
              : 'Ninguna cotización coincide con esa búsqueda'}
          </p>
          <p>
            {soloDelCliente
              ? 'Puede que esté a nombre de otro cliente, o que el documento de la matriz no sea el mismo que el de World Office. Marque «Buscar en todos los clientes».'
              : 'Solo salen las que no están enlazadas a otra orden y no están anuladas.'}
          </p>
        </div>
      ) : (
        <ul className={estilos.lista}>
          {candidatas.map((c) => {
            const elegida = escogida?.id_asiento === c.id_asiento
            const anterior = c.dias_despues !== null && c.dias_despues < 0

            const desplegada = abierta === c.id_asiento

            return (
              <li key={c.id_asiento} className={estilos.item}>
                {/* La cabeza es la fila en sí. El botón de desplegar se ancla
                    a ELLA y no al <li>, que cuando está abierto mide lo que
                    mida el documento entero: anclado ahí, «Ocultar» se iba al
                    fondo del panel y desaparecía justo cuando hace falta. */}
                <div className={estilos.cabeza}>
                  {/* El botón de desplegar va FUERA del de escoger y no dentro:
                    un botón no puede contener otro botón, y con el detalle
                    anidado el clic para mirar acabaría escogiendo. */}
                  <button
                    type="button"
                    className={estilos.lupa}
                    onClick={() => setAbierta(desplegada ? null : c.id_asiento)}
                    aria-expanded={desplegada}
                  >
                    {desplegada ? 'Ocultar el detalle' : 'Ver el detalle'}
                  </button>

                  <button
                    type="button"
                    className={[estilos.fila, elegida ? estilos.elegida : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setEscogida(elegida ? null : c)}
                    aria-pressed={elegida}
                  >
                    <span className={estilos.marca} aria-hidden="true" />

                    <span className={estilos.identidad}>
                      <b className={estilos.numero}>{c.numero_visible}</b>
                      <span className={estilos.nombre}>{c.cliente_nombre}</span>
                    </span>

                    <span className={estilos.plata}>{pesos(c.total)}</span>

                    <span className={estilos.cuando}>
                      <span className={estilos.fecha}>{fecha(c.fecha)}</span>
                      {c.dias_despues !== null && (
                        <span className={anterior ? estilos.antes : estilos.despues}>
                          {anterior
                            ? `${Math.abs(c.dias_despues)} días ANTES de la visita`
                            : c.dias_despues === 0
                            ? 'el mismo día de la visita'
                            : `${c.dias_despues} días después`}
                        </span>
                      )}
                    </span>

                    {/* La razón, escrita. No una estrellita. */}
                    {c.sugerida && c.motivo && <span className={estilos.motivo}>{c.motivo}</span>}

                    {!c.es_del_cliente && <span className={estilos.ajena}>Otro cliente</span>}
                  </button>
                </div>

                {desplegada && <DetalleCandidata idAsiento={c.id_asiento} />}
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

/**
 * El documento de World Office, abierto dentro de la fila.
 *
 * Se apoya en el mismo `ConceptosCotizacion` que dibuja la pantalla de
 * cotizaciones: si un día cambia cómo se lee una cotización, cambia en los dos
 * sitios a la vez. Aquí solo hace falta traer la fila completa, porque el
 * buscador de candidatas devuelve el resumen y no el documento.
 */
function DetalleCandidata({ idAsiento }: { idAsiento: number }) {
  const { data: cotizacion, isLoading, error } = useCotizacionWorldOffice(idAsiento)

  return (
    <div className={estilos.detalle}>
      {isLoading ? (
        <p className={estilos.vacio}>Abriendo el documento…</p>
      ) : error ? (
        <Aviso tipo="error">{error.message}</Aviso>
      ) : cotizacion ? (
        <ConceptosCotizacion cotizacion={cotizacion} />
      ) : (
        <Aviso tipo="alerta">
          World Office ya no devuelve esta cotización. Puede que la hayan anulado.
        </Aviso>
      )}
    </div>
  )
}
